import { manufacturingFetch } from "@/lib/manufacturingClient";
import { supabase } from "@/lib/supabase";
import type {
  MfgPoAttachmentKind,
  MfgPurchaseOrder,
  MfgPurchasingSettings,
  MfgVendor,
} from "@/lib/manufacturing/purchaseOrderTypes";

export type * from "@/lib/manufacturing/purchaseOrderTypes";

/** First-class PO vendor directory (not the alias typeahead at /api/mfg/vendors). */
export function listManufacturingVendors(opts?: { activeOnly?: boolean }) {
  const q = opts?.activeOnly ? "?active_only=1" : "";
  return manufacturingFetch<{ ok: true; vendors: MfgVendor[] }>(
    `/api/mfg/vendor-directory${q}`,
    { method: "GET" }
  );
}

export function createManufacturingVendor(payload: {
  name: string;
  primary_contact_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  is_active?: boolean;
  extra_contacts?: MfgVendor["extra_contacts"];
}) {
  return manufacturingFetch<{ ok: true; vendor: MfgVendor }>("/api/mfg/vendor-directory/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateManufacturingVendor(payload: {
  vendor_id: string;
  name?: string;
  primary_contact_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  is_active?: boolean;
  extra_contacts?: MfgVendor["extra_contacts"];
}) {
  return manufacturingFetch<{ ok: true; vendor: MfgVendor }>("/api/mfg/vendor-directory/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function deleteManufacturingVendor(payload: { vendor_id: string }) {
  return manufacturingFetch<{ ok: true; vendor_id: string }>("/api/mfg/vendor-directory/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getManufacturingPurchasingSettings() {
  return manufacturingFetch<{
    ok: true;
    settings: MfgPurchasingSettings;
    persisted: boolean;
  }>("/api/mfg/purchasing-settings", { method: "GET" });
}

export function upsertManufacturingPurchasingSettings(payload: Partial<MfgPurchasingSettings>) {
  return manufacturingFetch<{ ok: true; settings: MfgPurchasingSettings }>(
    "/api/mfg/purchasing-settings/upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function listManufacturingPurchaseOrders(opts?: { status?: string; plantId?: string }) {
  const params = new URLSearchParams();
  if (opts?.status) params.set("status", opts.status);
  if (opts?.plantId) params.set("plant_id", opts.plantId);
  const q = params.toString() ? `?${params}` : "";
  return manufacturingFetch<{ ok: true; orders: MfgPurchaseOrder[] }>(
    `/api/mfg/purchase-orders${q}`,
    { method: "GET" }
  );
}

export function getManufacturingPurchaseOrder(poId: string) {
  return manufacturingFetch<{ ok: true; order: MfgPurchaseOrder }>(
    `/api/mfg/purchase-orders/detail?po_id=${encodeURIComponent(poId)}`,
    { method: "GET" }
  );
}

export function createManufacturingPurchaseOrder(payload: Record<string, unknown>) {
  return manufacturingFetch<{ ok: true; order: MfgPurchaseOrder }>(
    "/api/mfg/purchase-orders/create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function updateManufacturingPurchaseOrder(payload: Record<string, unknown>) {
  return manufacturingFetch<{ ok: true; order: MfgPurchaseOrder }>(
    "/api/mfg/purchase-orders/update",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function finalizeManufacturingPurchaseOrder(poId: string) {
  return updateManufacturingPurchaseOrder({ po_id: poId, finalize: true });
}

export function archiveManufacturingPurchaseOrder(payload: { po_id: string; reason: string }) {
  return manufacturingFetch<{ ok: true; order: MfgPurchaseOrder }>(
    "/api/mfg/purchase-orders/archive",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function deleteManufacturingPurchaseOrder(payload: { po_id: string }) {
  return manufacturingFetch<{ ok: true; po_id: string }>("/api/mfg/purchase-orders/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function issueManufacturingPurchaseOrder(payload: {
  po_id: string;
  expected_delivery_on?: string | null;
}) {
  return manufacturingFetch<{ ok: true; order: MfgPurchaseOrder }>(
    "/api/mfg/purchase-orders/issue",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function acknowledgeManufacturingPlantRequest(payload: { po_id: string }) {
  return manufacturingFetch<{ ok: true; order: MfgPurchaseOrder }>(
    "/api/mfg/purchase-orders/acknowledge",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function receiveManufacturingPurchaseOrder(payload: {
  po_id: string;
  receipts: Array<{
    line_id: string;
    received_qty: number;
    quality_note?: string;
    quality_status?: "ok" | "off_quality" | "pending";
    chargeback_usd?: number | null;
    rate?: number;
  }>;
}) {
  return manufacturingFetch<{ ok: true; order: MfgPurchaseOrder }>(
    "/api/mfg/purchase-orders/receive",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function recordManufacturingPurchaseOrderInvoice(payload: {
  po_id: string;
  invoice_total_usd: number;
  invoice_pay_terms: string;
  invoice_delivery_on?: string | null;
  invoice_cc_emails?: string[];
}) {
  return manufacturingFetch<{ ok: true; order: MfgPurchaseOrder }>(
    "/api/mfg/purchase-orders/record-invoice",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export async function uploadPurchaseOrderAttachment(args: {
  po_id: string;
  kind: MfgPoAttachmentKind;
  file: File;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!supabase) return { ok: false, error: "App is not configured for sign-in." };
  const sign = await manufacturingFetch<{
    ok: true;
    bucket: string;
    path: string;
    token: string;
    content_type: string;
    kind: MfgPoAttachmentKind;
    po_id: string;
  }>("/api/mfg/purchase-orders/attachment", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "sign",
      po_id: args.po_id,
      kind: args.kind,
      content_type: args.file.type || "application/pdf",
      byte_size: args.file.size,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  const s = sign.data;
  const { error: upErr } = await supabase.storage
    .from(s.bucket)
    .uploadToSignedUrl(s.path, s.token, args.file, {
      contentType: s.content_type,
      upsert: false,
    });
  if (upErr) return { ok: false, error: upErr.message };
  const commit = await manufacturingFetch<{ ok: true }>("/api/mfg/purchase-orders/attachment", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "commit",
      po_id: s.po_id,
      kind: s.kind,
      path: s.path,
      bucket: s.bucket,
      content_type: s.content_type,
      byte_size: args.file.size,
      original_filename: args.file.name,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  return { ok: true };
}

/** Fetch the PDF captured when the PO was issued. 404 when none has been stored yet. */
export async function fetchStoredIssuedPurchaseOrderPdf(
  poId: string
): Promise<{ ok: true; blob: Blob } | { ok: false; error: string; status?: number }> {
  const signed = await manufacturingFetch<{ ok: true; url: string }>(
    "/api/mfg/purchase-orders/attachment",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ op: "download-issued", po_id: poId }),
    }
  );
  if (!signed.ok) return { ok: false, error: signed.error, status: signed.status };
  const file = await fetch(signed.data.url);
  if (!file.ok) return { ok: false, error: "Could not download the stored purchase order." };
  return { ok: true, blob: await file.blob() };
}
